/**
 * EL LOGO DEL NEGOCIO DESDE LA FICHA PUBLICA (ONB-LOGO-T1, contrato de A #33488, OK de P #33491, nexo-api 621).
 *
 * POST /api/v1/onboarding/logo multipart (token, archivo) -> 201 {success, data:{url}}; errores problem+json con code:
 * 400 TOKEN_REQUERIDO, ARCHIVO_REQUERIDO, ARCHIVO_INVALIDO; 404 TOKEN_NO_EXISTE; 409 ENLACE_CERRADO, LOGO_YA_EXISTE; 429 DEMASIADOS_INTENTOS.
 * El enlace pone el PRIMER logo y nunca lo reemplaza: despues de subirlo, la ficha ya no ofrece otro archivo.
 *
 * La regla del archivo NO se copia aqui: es la misma de la subida con sesion (application/negocios/archivoDeApariencia.js,
 * espejo de ReglasDeApariencia del API: JPEG o PNG por firma, hasta 5 MB). El API sigue siendo el que manda.
 *
 * El archivo no entra a captured, al borrador ni a responses: solo viaja en este POST. Lo que queda es la URL en la ficha
 * del negocio (account_config.logo_url), y eso lo escribe el API.
 *
 * Sin DOM: lo prueba scripts/check-onboard-logo.mjs.
 *
 * @module application/onboarding/logoOnboarding
 */

import { porQueNoSePuedeSubir, urlDeLaSubida } from '../negocios/archivoDeApariencia.js';

/** Lo que el panel revisa antes del viaje. null si se puede subir. */
export function porQueNoSubeElLogo(archivo, primerosBytes = null) {
  return porQueNoSePuedeSubir(archivo, 'logo', primerosBytes);
}

/** Cada code de A en una frase para el dueno; uno que no se conoce no se inventa ni se da por subido. */
export const MENSAJES_LOGO = {
  LOGO_SUBIDO: 'Listo: tu logo quedo guardado.',
  TOKEN_REQUERIDO: 'Este enlace ya no sirve. Pide uno nuevo a Nexo.',
  TOKEN_NO_EXISTE: 'Este enlace ya no sirve. Pide uno nuevo a Nexo.',
  ARCHIVO_REQUERIDO: 'Elige la imagen de tu logo.',
  ARCHIVO_INVALIDO: 'El logo tiene que ser una imagen PNG o JPEG de hasta 5 MB.',
  DEMASIADOS_INTENTOS: 'Hubo demasiados intentos. Espera unos minutos y vuelve a intentarlo.',
  // 409 (nexo-api 621 38ded4a, #33519): el enlace sube logo abierto o hasta 24 h tras completar; despues, desde el panel.
  ENLACE_CERRADO: 'Este enlace ya cerro. El logo lo cambias desde el panel de tu negocio.',
  // 409 (nexo-api 621 cf5d7f6, #33527): el enlace pone el primer logo y nunca lo reemplaza.
  LOGO_YA_EXISTE: 'Tu negocio ya tiene logo. Para cambiarlo, hazlo desde el panel de tu negocio.',
  // 410 (nexo-api 723, Ares #40212): el enlace vence a los 14 dias de creado; otro archivo daria lo mismo.
  ENLACE_VENCIDO: 'Este enlace ya no sirve. Pide uno nuevo a Nexo.'
};

/** Lo que se dice bajo un logo ya subido: el enlace no lo reemplaza (nexo-api 621 cf5d7f6). */
export const LOGO_QUEDA = 'Para cambiarlo despues, hazlo desde el panel de tu negocio.';

/** La respuesta del POST ({status, cuerpo}) en {ok, code, mensaje, url}. */
export function leerRespuestaDeLogo(r) {
  if (!r) return { ok: false, code: 'SIN_RED', mensaje: 'No pudimos hablar con Nexo. Revisa tu conexion e intenta otra vez.', url: null };
  if (r.status === 201 || r.status === 200) {
    const url = urlDeLaSubida({ data: r.cuerpo });
    // Un 2xx sin url no deja nada que mostrar: no se celebra.
    if (url) return { ok: true, code: 'LOGO_SUBIDO', mensaje: MENSAJES_LOGO.LOGO_SUBIDO, url };
    return { ok: false, code: 'SIN_URL', mensaje: 'No pudimos guardar el logo ahora. Intenta en un rato.', url: null };
  }
  const code = String(r.cuerpo?.code || r.cuerpo?.Code || '').trim();
  return { ok: false, code: code || 'ERROR', mensaje: MENSAJES_LOGO[code] || 'No pudimos guardar el logo ahora. Intenta en un rato.', url: null };
}

/**
 * ¿Ya esta servida la ruta? El sondeo es un multipart sin token ni archivo (no lleva ningun dato del dueno): servida
 * contesta 400 TOKEN_REQUERIDO (A lee el formulario a mano, nunca 415); sin servir, 404. Solo ese 400 enciende el campo.
 */
export function logoServidoSegun(r) {
  return !!r && r.status === 400 && String(r.cuerpo?.code || r.cuerpo?.Code || '') === 'TOKEN_REQUERIDO';
}
